import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ProjectAssignmentHandler, AssignmentResult } from '@luzula/luzula-lib';
import { DataService } from '../../data.service';
import { ProjectService } from '../../project.service';
import { AufnahmePunktDocument } from '../../data-model';

/**
 * Handler für Projekt-Zuweisungsoperationen für AufnahmePunktDocuments.
 * Kapselt alle datenmodell- und service-spezifischen Zugriffe.
 */
@Injectable({ providedIn: 'root' })
export class BohrpunktProjectAssignmentHandler implements ProjectAssignmentHandler {
	private ds = inject(DataService);
	private ps = inject(ProjectService);
	private msg = inject(ToastrService);

	/** Cache für zugeordnete Punkt-IDs (wird von MapComponent gesetzt) */
	private _assignedPointIds = new Set<string>();

	/**
	 * Setzt die IDs der zugeordneten Punkte.
	 * Wird von MapComponent aufgerufen wenn sich die Projektpunkte ändern.
	 */
	setAssignedPointIds(ids: Set<string>): void {
		this._assignedPointIds = ids;
	}

	/**
	 * Gibt die IDs aller dem aktiven Projekt zugeordneten Punkte zurück.
	 */
	getAssignedPointIds(): Set<string> {
		return this._assignedPointIds;
	}

	/**
	 * Prüft ob ein Punkt dem aktiven Projekt zugeordnet werden kann.
	 */
	canAssign(): boolean {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) return false;
		return this.ps.canAssignToProject();
	}

	/**
	 * Prüft ob ein Punkt aus dem aktiven Projekt entfernt werden kann.
	 */
	canRemove(pointId: string): boolean {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) return false;
		if (!this.ps.canAssignToProject()) return false;
		return this._assignedPointIds.has(pointId);
	}

	/**
	 * Ordnet einen Punkt dem aktiven Projekt zu.
	 */
	async assignToProject(pointId: string): Promise<AssignmentResult> {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) {
			return { success: false, message: 'Kein Projekt aktiv' };
		}

		try {
			// Vollständiges Dokument aus DB laden
			const fullDoc = await firstValueFrom(this.ds.getDocument$(pointId)) as AufnahmePunktDocument;
			if (!fullDoc?.header) {
				return { success: false, message: 'Punkt nicht gefunden' };
			}

			// Projects-Array initialisieren falls nicht vorhanden
			if (!fullDoc.header.projects) {
				fullDoc.header.projects = [];
			}

			// Prüfen ob bereits zugeordnet
			if (fullDoc.header.projects.some(p => p.id === projectDoc._id)) {
				return { success: false, message: 'Punkt ist bereits dem Projekt zugeordnet' };
			}

			// Projekt hinzufügen
			fullDoc.header.projects.push({ id: projectDoc._id, name: projectDoc.name });
			await this.ds.saveDocument(fullDoc);

			// Cache aktualisieren
			this._assignedPointIds.add(pointId);

			this.msg.success('Punkt wurde dem Projekt zugeordnet');
			return { success: true };
		} catch (error) {
			console.error('Fehler bei Projektzuordnung:', error);
			return { success: false, message: 'Fehler bei der Projektzuordnung' };
		}
	}

	/**
	 * Entfernt einen Punkt aus dem aktiven Projekt.
	 */
	async removeFromProject(pointId: string): Promise<AssignmentResult> {
		const projectId = this.ps.project();
		if (!projectId) {
			return { success: false, message: 'Kein Projekt aktiv' };
		}

		try {
			// Vollständiges Dokument aus DB laden
			const fullDoc = await firstValueFrom(this.ds.getDocument$(pointId)) as AufnahmePunktDocument;
			if (!fullDoc?.header?.projects) {
				return { success: false, message: 'Punkt nicht gefunden oder keine Projekte' };
			}

			// Projekt entfernen
			fullDoc.header.projects = fullDoc.header.projects.filter(p => p.id !== projectId);
			await this.ds.saveDocument(fullDoc);

			// Cache aktualisieren
			this._assignedPointIds.delete(pointId);

			return { success: true };
		} catch (error) {
			console.error('Fehler beim Entfernen aus Projekt:', error);
			return { success: false, message: 'Fehler beim Entfernen aus dem Projekt' };
		}
	}

	/**
	 * Löscht einen einzelnen Punkt.
	 */
	async deletePoint(pointId: string): Promise<AssignmentResult> {
		try {
			const response = await this.ds.removeBohrpunkt(pointId);
			if (response?.ok && response?.id) {
				return { success: true };
			}
			return { success: false, message: 'Punkt konnte nicht gelöscht werden' };
		} catch (error) {
			console.error('Fehler beim Löschen:', error);
			return { success: false, message: 'Fehler beim Löschen des Punktes' };
		}
	}

	/**
	 * Löscht mehrere Punkte.
	 */
	async deletePoints(pointIds: string[]): Promise<AssignmentResult> {
		try {
			await this.ds.removeBohrpunkteBulk(pointIds);
			this.msg.success(`${pointIds.length} Punkte wurden gelöscht`);
			return { success: true };
		} catch (error) {
			console.error('Fehler beim Bulk-Löschen:', error);
			return { success: false, message: 'Fehler beim Löschen der Punkte' };
		}
	}
}
